import React, { useState, useEffect } from 'react';
import { ArrowRight, Download, Linkedin, Mail, X } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Hero: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Image rotation state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = [
    '/kennedy1.jpg',
    '/kennedy2.jpg', 
    '/kennedy3.jpg',
    '/kennedy4.jpg',
    '/kennedy5.jpg',
    '/kennedy6.jpg'
  ];

  // Rotate images every 3.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3500);

    return () => clearInterval(interval);
  }, [images.length]);

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center p-4 pt-20 md:pt-20 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20 animate-float ${
          isDark ? 'bg-sunrise-orange' : 'bg-deep-blue'
        }`}></div>
        <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-10 animate-float delay-300 ${
          isDark ? 'bg-serengeti-gold' : 'bg-sunrise-orange'
        }`}></div>
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-5 animate-float delay-500 ${
          isDark ? 'bg-deep-blue' : 'bg-serengeti-gold'
        }`}></div>
      </div>

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:items-stretch gap-8 lg:gap-0">
        {/* Left side - Content */}
        <div className="w-full lg:w-1/2 text-center lg:text-left animate-fade-in-up">
          <div className={`glass border rounded-3xl lg:rounded-l-3xl lg:rounded-tr-none lg:rounded-br-none p-8 lg:p-12 shadow-2xl h-full ${
            isDark ? 'glass-dark' : 'glass-light'
          }`}>
            <div className="mb-6">
              <span className={`text-sm font-medium px-4 py-2 rounded-full ${
                isDark 
                  ? 'bg-sunrise-orange/20 text-sunrise-orange border border-sunrise-orange/30' 
                  : 'bg-deep-blue/10 text-deep-blue border border-deep-blue/20'
              }`}>
                Strategic Communications Consultant
              </span>
            </div>
            
            <h1 className={`text-4xl md:text-6xl lg:text-7xl font-light mb-6 tracking-tight ${
              isDark ? 'text-gray-100' : 'text-slate-800'
            }`}>
              Kennedy
              <span className="block gradient-text font-medium">Mmari</span>
            </h1>
            
            <p className={`text-xl lg:text-2xl mb-8 font-light leading-relaxed ${
              isDark ? 'text-gray-200' : 'text-slate-600'
            }`}>
              Transforming organizations through <span className="gradient-text font-medium">strategic communications</span>, 
              public relations excellence, and digital innovation.
            </p>
            
            <p className={`text-lg mb-12 max-w-2xl font-light ${
              isDark ? 'text-gray-300' : 'text-slate-500'
            }`}>
              Leading strategic communications initiatives with over a decade of expertise 
              in public relations, digital media management, and stakeholder engagement across Tanzania.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <button 
                onClick={() => scrollToSection('#services')}
                className={`group glass border px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-3 font-medium hover:scale-105 cursor-pointer ${
                  isDark 
                    ? 'glass-dark glass-hover-dark text-gray-100' 
                    : 'glass-light glass-hover-light text-slate-700'
                }`}
              >
                View Services
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button className={`glass border px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-3 font-medium hover:scale-105 cursor-pointer ${
                isDark 
                  ? 'glass-dark glass-hover-dark text-gray-100' 
                  : 'glass-light glass-hover-light text-slate-700'
              }`}>
                <Download size={20} />
                Download CV
              </button>
            </div>

            {/* Social Links and Signature */}
            <div className="flex items-center gap-4 justify-center lg:justify-start">
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kennedymmari" className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isDark 
                    ? 'glass-dark glass-hover-dark text-gray-100' 
                    : 'glass-light glass-hover-light text-slate-700'
                }`}>
                  <Linkedin size={20} />
                </a>
                <a href="#" className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isDark 
                    ? 'glass-dark glass-hover-dark text-gray-100' 
                    : 'glass-light glass-hover-light text-slate-700'
                }`}>
                  <X size={20} />
                </a>
                <a href="mailto:mmarikennedy@gmail.com" className={`p-3 rounded-full glass border transition-all duration-300 hover:scale-110 cursor-pointer ${
                  isDark 
                    ? 'glass-dark glass-hover-dark text-gray-100' 
                    : 'glass-light glass-hover-light text-slate-700'
                }`}>
                  <Mail size={20} />
                </a>
              </div>
              
              {/* Signature */}
              <div className="ml-4">
                <img 
                  src="/MmariSignature.png" 
                  alt="Kennedy Mmari Signature" 
                  className="h-8 w-auto opacity-80 hover:opacity-100 transition-opacity duration-300"
                  onError={(e) => {
                    // Hide signature if image doesn't exist
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Profile Image */}
        <div className="w-full lg:w-1/2 flex justify-center lg:justify-start animate-slide-in-right">
          <div className="w-full h-80 md:h-96 lg:h-full rounded-3xl lg:rounded-r-3xl lg:rounded-tl-none lg:rounded-bl-none overflow-hidden relative">
            {images.map((imageSrc, index) => (
              <img 
                key={imageSrc}
                src={imageSrc} 
                alt="Kennedy Mmari - Strategic Communications Consultant" 
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                  index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
                onError={(e) => {
                  // Fallback to a placeholder if image doesn't exist
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `
                    <div class="absolute inset-0 w-full h-full flex items-center justify-center ${isDark ? 'bg-gray-800' : 'bg-gray-200'}">
                      <span class="text-6xl font-bold gradient-text">KM</span>
                    </div>
                  `;
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
